"use server";

// Entrar, salir, la cuenta propia y la gestión de usuarios (solo admin).
// Las reglas viven en lib/usuarios.ts; aquí van los permisos y la sesión.

import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { revalidatePath } from "next/cache";
import { AUTH_ACTIVADA, contrasenaArranqueValida, ipDelCliente } from "./auth";
import { abrirSesion, cerrarSesion, exigirAdmin, usuarioActual } from "./sesion";
import { FrenoIntentos, contrasenaProvisional, normalizarEmail } from "./sesion-token";
import {
  actualizarUsuario, cerrarSesiones, comprobarCredenciales, contrasenaActualValida, crearUsuario,
  getUsuario, hayUsuarios, ponerContrasena, type Rol,
} from "./usuarios";

const globalFreno = globalThis as unknown as { __frenoLogin?: FrenoIntentos };
const freno = (globalFreno.__frenoLogin ??= new FrenoIntentos());

async function esHttps(): Promise<boolean> {
  const h = await headers();
  return h.get("x-forwarded-proto") === "https" || (process.env.HUB_URL_PUBLICA ?? "").startsWith("https://");
}

/** La IP real, no la que escriba el cliente en X-Forwarded-For (lib/auth.ts). */
async function ip(): Promise<string> {
  return ipDelCliente(await headers());
}

/** Solo rutas internas: un `siguiente` a otro dominio sería una redirección abierta. */
function destinoSeguro(siguiente: string): string {
  return siguiente.startsWith("/") && !siguiente.startsWith("//") && !siguiente.startsWith("/login") ? siguiente : "/";
}

export async function entrar(formData: FormData) {
  const email = normalizarEmail(String(formData.get("email") ?? ""));
  const contrasena = String(formData.get("password") ?? "");
  const siguiente = destinoSeguro(String(formData.get("siguiente") ?? "/"));
  const clave = `${email}|${await ip()}`;
  const volver = (error: string) =>
    redirect(`/login?error=${error}${siguiente !== "/" ? `&siguiente=${encodeURIComponent(siguiente)}` : ""}`);

  if (freno.bloqueado(clave)) volver("bloqueado");
  const u = comprobarCredenciales(email, contrasena);
  if (!u) {
    freno.fallo(clave);
    // un segundo por intento fallido: la fuerza bruta deja de salir a cuenta
    await new Promise((r) => setTimeout(r, 1000));
    volver("1");
    return;
  }
  freno.limpiar(clave);
  await abrirSesion(u, await esHttps());
  redirect(u.debeCambiar ? "/cuenta?primera=1" : siguiente);
}

/** Primer arranque: sin ningún usuario, AGENCIA_PASSWORD autoriza a crear el admin. */
export async function crearPrimerAdmin(formData: FormData) {
  if (!AUTH_ACTIVADA || hayUsuarios()) redirect("/login");
  const clave = `arranque|${await ip()}`;
  if (freno.bloqueado(clave)) redirect("/login?error=bloqueado");
  if (!contrasenaArranqueValida(String(formData.get("arranque") ?? ""))) {
    freno.fallo(clave);
    redirect("/login?error=arranque");
  }
  const contrasena = String(formData.get("password") ?? "");
  if (contrasena !== String(formData.get("password2") ?? "")) redirect("/login?error=distintas");
  const r = crearUsuario({
    nombre: String(formData.get("nombre") ?? ""), email: String(formData.get("email") ?? ""),
    rol: "admin", contrasena, provisional: false,
  });
  if (!r.ok) redirect(`/login?error=alta&detalle=${encodeURIComponent(r.error)}`);
  freno.limpiar(clave);
  await abrirSesion(r.datos, await esHttps());
  redirect("/usuarios");
}

export async function salir() {
  await cerrarSesion();
  redirect("/login");
}

// ─── la cuenta propia ─────────────────────────────────────────────────────

export type Respuesta = { ok: true; mensaje?: string } | { ok: false; error: string };

export async function cambiarMiContrasena(_: Respuesta | null, formData: FormData): Promise<Respuesta> {
  const yo = await usuarioActual();
  if (!yo) return { ok: false, error: "No hay sesión." };
  const actual = String(formData.get("actual") ?? "");
  const nueva = String(formData.get("nueva") ?? "");
  // con la PROVISIONAL no se vuelve a pedir: acaba de entrar con ella, y Chrome
  // rellenaba ese campo con otra contraseña guardada y daba «no es correcta»
  // (5 oct 2026, Lucía). Para cambiar una contraseña propia, sí se pide.
  if (!yo.debeCambiar && !contrasenaActualValida(yo.id, actual)) return { ok: false, error: "La contraseña actual no es correcta." };
  if (nueva !== String(formData.get("nueva2") ?? "")) return { ok: false, error: "Las dos contraseñas nuevas no coinciden." };
  if (contrasenaActualValida(yo.id, nueva)) return { ok: false, error: "La nueva tiene que ser distinta de la actual." };
  const r = ponerContrasena(yo.id, nueva, false);
  if (!r.ok) return r;
  // la versión ha subido: se reabre la sesión en ESTE navegador, las demás quedan cerradas
  await abrirSesion(getUsuario(yo.id)!, await esHttps());
  return { ok: true, mensaje: "Contraseña cambiada. Las sesiones de otros dispositivos se han cerrado." };
}

export async function cambiarMiNombre(_: Respuesta | null, formData: FormData): Promise<Respuesta> {
  const yo = await usuarioActual();
  if (!yo) return { ok: false, error: "No hay sesión." };
  const r = actualizarUsuario(yo.id, { nombre: String(formData.get("nombre") ?? "") });
  if (!r.ok) return r;
  revalidatePath("/", "layout");
  return { ok: true, mensaje: "Guardado." };
}

export async function cerrarMisOtrasSesiones(): Promise<Respuesta> {
  const yo = await usuarioActual();
  if (!yo) return { ok: false, error: "No hay sesión." };
  cerrarSesiones(yo.id);
  // también las conexiones de Claude (lib/conector)
  (await import("./conector/tokens")).revocarConexion(yo.id);
  await abrirSesion(getUsuario(yo.id)!, await esHttps());
  return { ok: true, mensaje: "Sesiones cerradas en el resto de dispositivos." };
}

// ─── gestión de usuarios (admin) ──────────────────────────────────────────

export type RespuestaAlta = { ok: true; email: string; provisional: string } | { ok: false; error: string };

export async function darDeAltaUsuario(_: RespuestaAlta | null, formData: FormData): Promise<RespuestaAlta> {
  await exigirAdmin();
  const provisional = contrasenaProvisional();
  const r = crearUsuario({
    nombre: String(formData.get("nombre") ?? ""), email: String(formData.get("email") ?? ""),
    rol: formData.get("rol") === "admin" ? "admin" : "equipo", contrasena: provisional, provisional: true,
  });
  if (!r.ok) return r;
  revalidatePath("/usuarios");
  return { ok: true, email: r.datos.email, provisional };
}

export async function cambiarRol(id: string, rol: Rol): Promise<Respuesta> {
  await exigirAdmin();
  const r = actualizarUsuario(id, { rol });
  revalidatePath("/usuarios");
  return r.ok ? { ok: true } : r;
}

export async function cambiarActivo(id: string, activo: boolean): Promise<Respuesta> {
  const yo = await exigirAdmin();
  if (yo && yo.id === id && !activo) return { ok: false, error: "No puedes desactivarte a ti mismo." };
  const r = actualizarUsuario(id, { activo });
  if (r.ok && !activo) (await import("./conector/tokens")).revocarConexion(id);
  revalidatePath("/usuarios");
  return r.ok ? { ok: true } : r;
}

export async function restablecerContrasena(id: string): Promise<{ ok: true; provisional: string } | { ok: false; error: string }> {
  await exigirAdmin();
  const provisional = contrasenaProvisional();
  const r = ponerContrasena(id, provisional, true);
  // contraseña nueva = fuera también sus conexiones de Claude (lib/conector)
  if (r.ok) (await import("./conector/tokens")).revocarConexion(id);
  // y fuera su bloqueo por intentos fallidos: si no, con la contraseña nueva
  // seguía sin poder entrar hasta pasados 15 minutos (5 oct 2026, Lucía)
  const email = getUsuario(id)?.email;
  if (r.ok && email) freno.limpiarPrefijo(`${email}|`);
  revalidatePath("/usuarios");
  return r.ok ? { ok: true, provisional } : r;
}

/** Qué opciones del menú NO ve el rol «equipo» (tarjeta de /usuarios). */
export async function guardarMenuEquipo(ocultas: string[]): Promise<Respuesta> {
  await exigirAdmin();
  const { guardarMenuOcultoEquipo } = await import("./menu-equipo");
  guardarMenuOcultoEquipo(ocultas);
  revalidatePath("/", "layout");
  return { ok: true };
}
